import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Grid,
  Typography,
  InputAdornment,
  CircularProgress,
  Alert,
  MenuItem,
  Chip,
} from "@mui/material";
import { UrlApi } from "../../../services/apirest";
import Notify from "../../Notify";
import useBusquedaClientes from "../clientesEnvasado/BusquedaCliente/useBusquedaClientes";
import LinkPagoGeneradoEnvasado from "./componentes/LinkPagoGeneradoEnvasado";
import {
  crearLinkEnvasadoCc,
  anularLinkEnvasadoCc,
  mensajeError,
} from "./services/linkEnvasadoMp.service";

const REFERENCIA_MAX = 20;

// Búsqueda de clientes en el servidor (sucursales autorizadas del usuario).
// "razon_social" también busca por sisven. Constante fuera del componente para que
// el debounce del hook no se recree en cada render.
const FILTROS_BUSQUEDA = ["razon_social", "cuit", "sisven"];
const MIN_CARACTERES_BUSQUEDA = 2;

const textoCliente = (c) => (c?.razon_social ? `${c.razon_social} - ${c.id}` : "");

const detalleCliente = (c) =>
  [
    c.sisven_id ? `Sisven: ${c.sisven_id}` : null,
    c.cuit ? `CUIT: ${c.cuit}` : null,
    c.sucursal ? `Sucursal: ${c.sucursal}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

// Cuotas (solo admin puede cambiarlas; si no, la API usa el default: 12, sin interés 2 a 6)
const CUOTAS_MAX_PERMITIDAS = 12;
const CUOTAS_DEFAULT = { max: 12, sinInteresHasta: 6 };
const SIN_INTERES_NINGUNA = "ninguna";
// Topes de cuotas sin interés que acepta Mercado Pago (otro valor da error)
const CUOTAS_SIN_INTERES_PERMITIDAS = [3, 6, 9, 12];

const GenerarLinkPagoEnvasado = ({ open, onClose, onGenerado }) => {
  const esAdmin = localStorage.getItem("role") === "admin";

  const [cliente, setCliente] = useState(null);
  const [textoBusqueda, setTextoBusqueda] = useState("");
  const [monto, setMonto] = useState("");
  const [referencia, setReferencia] = useState("");
  const [cuotasMax, setCuotasMax] = useState(CUOTAS_DEFAULT.max);
  const [sinInteresHasta, setSinInteresHasta] = useState(CUOTAS_DEFAULT.sinInteresHasta);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentLink, setPaymentLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const [notify, setNotify] = useState({
    open: false,
    mensaje: "",
    severity: "success",
    color: "#D4EFDF",
  });

  const {
    resultados,
    cargando: buscandoClientes,
    error: errorBusqueda,
    debouncedSearch,
    limpiarResultados,
  } = useBusquedaClientes(FILTROS_BUSQUEDA);

  useEffect(() => {
    if (!open) return;
    // Reset al abrir
    setCliente(null);
    setTextoBusqueda("");
    limpiarResultados();
    setMonto("");
    setReferencia("");
    setCuotasMax(CUOTAS_DEFAULT.max);
    setSinInteresHasta(CUOTAS_DEFAULT.sinInteresHasta);
    setError(null);
    setPaymentLink(null);
    setLinkCopied(false);
  }, [open, limpiarResultados]);

  // Cancela una búsqueda pendiente al desmontar
  useEffect(() => () => debouncedSearch.cancel?.(), [debouncedSearch]);

  // Solo se busca cuando el usuario escribe (no al elegir una opción ni al limpiar)
  const cambiarTextoBusqueda = (e, valor, reason) => {
    setTextoBusqueda(valor);
    if (reason !== "input") return;
    if (valor.trim().length >= MIN_CARACTERES_BUSQUEDA) {
      debouncedSearch(valor);
    } else {
      debouncedSearch.cancel?.();
      limpiarResultados();
    }
  };

  // El cliente elegido se mantiene entre las opciones aunque cambien los resultados
  const opcionesClientes =
    cliente && !resultados.some((c) => c.id === cliente.id)
      ? [cliente, ...resultados]
      : resultados;

  const textoSinOpciones =
    textoBusqueda.trim().length < MIN_CARACTERES_BUSQUEDA
      ? `Escriba al menos ${MIN_CARACTERES_BUSQUEDA} caracteres`
      : errorBusqueda
      ? "No se pudieron buscar clientes"
      : "Cliente inexistente";

  const montoNum = Number(monto);
  const formValido =
    cliente &&
    Number.isFinite(montoNum) &&
    Number(montoNum.toFixed(2)) > 0 &&
    referencia.trim() !== "" &&
    referencia.length <= REFERENCIA_MAX;

  const handleGenerar = async () => {
    if (!formValido) return;
    setLoading(true);
    setError(null);

    let idRegistro = null;
    // La empresa (cuenta de Mercado Pago) la define el backend con la sucursal del cliente
    let idEmpresa = null;
    const textoReferencia = referencia.trim();
    try {
      // 1. Crear el registro en la base (estado 'generado')
      const respRegistro = await fetch(UrlApi + "/envasado_link_pago_cc/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente: cliente.id,
          monto: Number(montoNum.toFixed(2)),
          referencia: textoReferencia || null,
          usuario: localStorage.getItem("user"),
        }),
      });
      const dataRegistro = await respRegistro.json().catch(() => ({}));
      if (!respRegistro.ok) {
        throw new Error(dataRegistro.message || "No se pudo registrar el link de pago");
      }
      idRegistro = dataRegistro.id;
      idEmpresa = dataRegistro.id_empresa ?? null;

      // 2. Generar el link en Mercado Pago. La API toma monto y referencia del registro,
      // arma el external_reference (elc_<id>) y guarda link y order_id en el registro.
      // Solo un admin envía cuotas; para el resto la API aplica el default.
      const cuotas = esAdmin
        ? {
            max: cuotasMax,
            sinInteresHasta:
              sinInteresHasta === SIN_INTERES_NINGUNA ? null : sinInteresHasta,
          }
        : undefined;
      const { checkout_url, order_id, external_reference } = await crearLinkEnvasadoCc(
        idEmpresa,
        idRegistro,
        cuotas
      );

      setPaymentLink({
        url: checkout_url,
        external_reference,
        order_id,
        referencia: textoReferencia,
      });
      setNotify({
        open: true,
        mensaje: "Link de pago generado",
        severity: "success",
        color: "#D4EFDF",
      });
      onGenerado && onGenerado();
    } catch (err) {
      console.error("Error generando link de pago envasado:", err);
      // Si el registro quedó creado, se anula para no dejarlo huérfano. La API además
      // cancela la order en MP si llegó a crearse; si la API no responde, se anula en la base.
      if (idRegistro) {
        anularLinkEnvasadoCc(idEmpresa, idRegistro).catch(() =>
          fetch(UrlApi + "/envasado_link_pago_cc/anular/" + idRegistro, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
          }).catch(() => {})
        );
      }
      setError(mensajeError(err, "Error generando el link de pago"));
    } finally {
      setLoading(false);
    }
  };

  const copiarLink = () => {
    if (!paymentLink?.url) return;
    navigator.clipboard
      .writeText(paymentLink.url)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch(() => setError("No se pudo copiar al portapapeles"));
  };

  return (
    <>
      {/* Mientras se genera el link no se permite cerrar, para no cortar el proceso a mitad */}
      <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
        <Box backgroundColor="#1F618D">
          <DialogTitle>
            <Typography variant="h6" align="center" color="#ffffff">
              Generar link de pago - cuenta corriente
            </Typography>
          </DialogTitle>
        </Box>

        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : paymentLink ? (
            <LinkPagoGeneradoEnvasado
              paymentLink={paymentLink}
              linkCopied={linkCopied}
              onCopiarLink={copiarLink}
            />
          ) : (
            <Grid container spacing={2} mt={0}>
              <Grid item xs={12}>
                <Autocomplete
                  options={opcionesClientes}
                  value={cliente}
                  inputValue={textoBusqueda}
                  loading={buscandoClientes}
                  loadingText="Buscando..."
                  noOptionsText={textoSinOpciones}
                  // El filtrado lo hace el servidor
                  filterOptions={(x) => x}
                  getOptionLabel={textoCliente}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  onChange={(e, value) => setCliente(value)}
                  onInputChange={cambiarTextoBusqueda}
                  renderOption={(props, c) => (
                    <li {...props} key={c.id}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={1}
                        width="100%"
                      >
                        <Box>
                          <Typography variant="body2">{textoCliente(c)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {detalleCliente(c)}
                          </Typography>
                        </Box>
                        {c.empresa ? (
                          <Chip
                            label={c.empresa}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500, flexShrink: 0 }}
                          />
                        ) : null}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      placeholder="Razón social, CUIT o Sisven"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {buscandoClientes ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Monto"
                  type="number"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Referencia"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value.slice(0, REFERENCIA_MAX))}
                  inputProps={{ maxLength: REFERENCIA_MAX }}
                  helperText={`${referencia.length}/${REFERENCIA_MAX}`}
                />
              </Grid>

              {esAdmin && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Cuotas máximas"
                      value={cuotasMax}
                      onChange={(e) => {
                        const max = Number(e.target.value);
                        setCuotasMax(max);
                        // El rango sin interés no puede superar el máximo de cuotas
                        // (se pasa al mayor tope permitido que entre, o a ninguna)
                        if (sinInteresHasta !== SIN_INTERES_NINGUNA && sinInteresHasta > max) {
                          const tope = CUOTAS_SIN_INTERES_PERMITIDAS.filter((n) => n <= max).pop();
                          setSinInteresHasta(tope || SIN_INTERES_NINGUNA);
                        }
                      }}
                    >
                      {Array.from({ length: CUOTAS_MAX_PERMITIDAS }, (_, i) => i + 1).map((n) => (
                        <MenuItem key={n} value={n}>
                          {n === 1 ? "1 (sin cuotas)" : n}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Sin interés"
                      value={sinInteresHasta}
                      onChange={(e) =>
                        setSinInteresHasta(
                          e.target.value === SIN_INTERES_NINGUNA
                            ? SIN_INTERES_NINGUNA
                            : Number(e.target.value)
                        )
                      }
                    >
                      <MenuItem value={SIN_INTERES_NINGUNA}>Ninguna (todas con interés)</MenuItem>
                      {CUOTAS_SIN_INTERES_PERMITIDAS.filter((n) => n <= cuotasMax).map((n) => (
                        <MenuItem key={n} value={n}>
                          De 2 a {n} cuotas
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Alert severity="info">
                  El pago del link no se imputa automáticamente: el recibo en la cuenta
                  corriente del cliente se carga por separado.
                </Alert>
              </Grid>

              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="error" variant="outlined" disabled={loading}>
            {paymentLink ? "Cerrar" : "Cancelar"}
          </Button>
          {!paymentLink && (
            <Button
              onClick={handleGenerar}
              color="success"
              variant="contained"
              disabled={!formValido || loading}
            >
              Generar link
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {notify.open && (
        <Notify
          mensaje={notify.mensaje}
          open={notify.open}
          color={notify.color}
          severity={notify.severity}
          handleClose={() => setNotify({ ...notify, open: false })}
        />
      )}
    </>
  );
};

export default GenerarLinkPagoEnvasado;
